import type { Node } from 'prosemirror-model'
import type { Decoration } from 'prosemirror-view'

import { kAgentV2SourceUnavailableLabel, kAgentV2UnverifiedSourceLabel } from '@/agent-v2/references/agent-v2-citation-labels'

type AgentV2ReferenceMarkerAttrs = {
  citationId: string
  label: string
  isResolved: boolean
  canOpen: boolean
}

export class AgentV2ReferenceMarkerView {
  dom: HTMLSpanElement

  private clickHandler: (() => void) | null = null
  private highlightClasses: string | null = null

  constructor(node: Node, private readonly onCitationClick: (citationId: string) => void, decorations: readonly Decoration[]) {
    this.dom = document.createElement('span')
    this.highlightClasses = extractHighlightClasses(decorations)
    this.applyAttributes(node)
  }

  private applyAttributes(node: Node): void {
    const { citationId, label, isResolved, canOpen } = node.attrs as AgentV2ReferenceMarkerAttrs

    this.dom.contentEditable = 'false'
    this.dom.textContent = `[${label}]`
    this.removeClickHandler()

    if (!isResolved) {
      this.setClassName('reference-marker-badge text-brand-neutral-600 cursor-default')
      this.dom.setAttribute('aria-label', kAgentV2UnverifiedSourceLabel)
      this.dom.setAttribute('title', kAgentV2UnverifiedSourceLabel)
      this.dom.setAttribute('aria-disabled', 'true')
      return
    }

    if (!canOpen) {
      this.setClassName('reference-marker-badge text-brand-neutral-600 cursor-not-allowed')
      this.dom.setAttribute('aria-label', `Reference ${label}: source unavailable`)
      this.dom.setAttribute('title', kAgentV2SourceUnavailableLabel)
      this.dom.setAttribute('aria-disabled', 'true')
      return
    }

    this.setClassName('reference-marker-badge text-brand-700 hover:text-brand-800 cursor-pointer')
    this.dom.setAttribute('aria-label', `Open reference ${label}`)
    this.dom.setAttribute('title', 'Open source')
    this.dom.removeAttribute('aria-disabled')
    this.clickHandler = () => this.onCitationClick(citationId)
    this.dom.addEventListener('click', this.clickHandler)
  }

  private removeClickHandler(): void {
    if (!this.clickHandler) return
    this.dom.removeEventListener('click', this.clickHandler)
    this.clickHandler = null
  }

  stopEvent(): boolean {
    return true
  }

  ignoreMutation(): boolean {
    return true
  }

  update(node: Node, decorations: readonly Decoration[]): boolean {
    if (node.type.name !== 'referenceMarker') return false
    this.highlightClasses = extractHighlightClasses(decorations)
    this.applyAttributes(node)
    return true
  }

  destroy(): void {
    this.removeClickHandler()
  }

  private setClassName(baseClass: string): void {
    this.dom.className = this.highlightClasses ? `${baseClass} ${this.highlightClasses}` : baseClass
  }
}

/** A marker can carry more than one highlight at once, e.g. an open citation inside an attached selection. */
function extractHighlightClasses(decorations: readonly Decoration[]): string | null {
  const classNames = decorations
    .map((decoration) => (decoration.spec as { class?: string } | undefined)?.class)
    .filter((className) => typeof className === 'string')

  return classNames.length > 0 ? classNames.join(' ') : null
}
